import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Tab, Tabs, CircularProgress, Typography, useTheme } from '@mui/material';
import {
  Monitor, Search, MessageSquare, FileText, PenTool, Smartphone, Activity,
} from 'lucide-react';

import { analyticsApi } from '../../../../api/analyticsApi';
import UserAnalyticsHeader from './UserAnalyticsHeader';
import UserAnalyticsSummary from './UserAnalyticsSummary';
import UserActivityChart from './UserActivityChart';
import UserFeatureUsage from './UserFeatureUsage';
import UserSessionsTab from './UserSessionsTab';
import UserSearchesTab from './UserSearchesTab';
import UserConversationsTab from './UserConversationsTab';
import UserContractsTab from './UserContractsTab';
import UserSignaturesTab from './UserSignaturesTab';
import UserDevicesTab from './UserDevicesTab';
import UserActivityFeed from './UserActivityFeed';

const TABS = [
  { key: 'sessions', label: 'Sessions', icon: <Monitor size={16} /> },
  { key: 'searches', label: 'Searches', icon: <Search size={16} /> },
  { key: 'conversations', label: 'Conversations', icon: <MessageSquare size={16} /> },
  { key: 'contracts', label: 'Contracts', icon: <FileText size={16} /> },
  { key: 'signatures', label: 'Signatures', icon: <PenTool size={16} /> },
  { key: 'devices', label: 'Devices', icon: <Smartphone size={16} /> },
  { key: 'activity', label: 'Activity Feed', icon: <Activity size={16} /> },
];

const UserAnalyticsPage = () => {
  const { orgId, userId } = useParams();
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('sessions');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: result } = await analyticsApi.getUserComprehensive(userId);
        setData(result);
      } catch (err) {
        console.error('Failed to load user analytics:', err);
        setError(err.message || 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error || 'User analytics not available.'}</Typography>
      </Box>
    );
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'sessions': return <UserSessionsTab sessions={data.sessions} />;
      case 'searches': return <UserSearchesTab searches={data.searches} />;
      case 'conversations': return <UserConversationsTab conversations={data.conversations} />;
      case 'contracts': return <UserContractsTab contracts={data.contracts} />;
      case 'signatures': return <UserSignaturesTab signatures={data.signatures} />;
      case 'devices': return <UserDevicesTab devices={data.devices} />;
      case 'activity': return <UserActivityFeed userId={userId} />;
      default: return null;
    }
  };

  return (
    <Box sx={{ py: 2, height: '100%', overflow: 'auto' }}>
      <UserAnalyticsHeader user={data.user} orgId={orgId} />
      <UserAnalyticsSummary summary={data.summary} />

      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 500px', minWidth: 0 }}>
          <UserActivityChart timeline={data.activity_timeline} />
        </Box>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <UserFeatureUsage featureUsage={data.feature_usage} />
        </Box>
      </Box>

      {/* Tabbed Detail Section */}
      <Box sx={{
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        overflow: 'hidden',
      }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 48,
              fontSize: '0.8rem',
              fontWeight: 500,
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {tab.icon}
                  {tab.label}
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box sx={{ p: 3 }}>
          {renderTab()}
        </Box>
      </Box>
    </Box>
  );
};

export default UserAnalyticsPage;
